import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useAuth0 } from '@auth0/auth0-react';
import { useDispatch } from 'react-redux';
import { env } from '../../../shared/config/env';
import { buildAuth0TokenParams } from '../lib/buildAuth0TokenParams';
import {
  authCleared,
  authSyncFailed,
  authSyncStarted,
  authSyncSucceeded,
} from '../store/authSlice';
import { loadCurrentUser } from '../store/authThunks';

export function useAuthSync() {
  const dispatch = useDispatch();
  const { isLoading, isAuthenticated, getAccessTokenSilently, user } = useAuth0();

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (!isAuthenticated) {
      dispatch(authCleared());
      Sentry.setUser(null);
      return;
    }

    let isCancelled = false;

    const syncAuthSession = async () => {
      dispatch(authSyncStarted());

      try {
        const accessToken = await getAccessTokenSilently(
          buildAuth0TokenParams({
            audience: env.auth0.audience,
          }),
        );

        if (isCancelled) {
          return;
        }

        dispatch(authSyncSucceeded({ accessToken }));
        Sentry.setUser({ email: user?.email });

        if (!isCancelled) dispatch(loadCurrentUser());
      } catch {
        if (isCancelled) {
          return;
        }

        dispatch(authSyncFailed());
      }
    };

    syncAuthSession();

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resync on auth state only, not on email changes
  }, [dispatch, getAccessTokenSilently, isAuthenticated, isLoading]);
}
